import { find, get, isEmpty } from '../lodash';
import {
  PERM_ACTIVITY_GREEN_LIGHT,
  PERM_ACTIVITY_DOCUMENTATION,
  PERM_ACTIVITY_REPORT,
  PERM_ACTIVITY_DUPLICATE,
} from '../constant';

// ── TAK-2080: the Duplicate permission ──────────────────────────────────────

// Presets whose members can never be allowed to duplicate.
const DUPLICATE_LOCKED_PRESETS = ['documentor', 'reportOnly'];

const isDuplicateEntry = (entry) =>
  get(entry, 'state') === PERM_ACTIVITY_DUPLICATE;

/** Whether a member's permissions list allows duplicating (missing = no). */
export const hasDuplicatePermission = (permissions = []) =>
  Boolean(
    get(find(permissions || [], isDuplicateEntry), 'permission.read', false)
  );

/** The page permissions only, without the Duplicate entry. */
export const withoutDuplicatePermission = (permissions = []) =>
  (permissions || []).filter((entry) => !isDuplicateEntry(entry));

/** The page permissions with the Duplicate entry set and placed last. */
export const withDuplicatePermission = (permissions = [], enabled = false) => [
  ...withoutDuplicatePermission(permissions),
  {
    name: 'Duplicate',
    state: PERM_ACTIVITY_DUPLICATE,
    permission: { read: Boolean(enabled), write: false },
  },
];

/** Default for the Duplicate box when a preset is chosen: on for Collaborator. */
export const duplicateDefaultForPreset = (preset) => preset === 'collaborator';

/** The Duplicate box is greyed out with no preset, Documentor or Report only. */
export const isDuplicateLocked = (preset) =>
  !preset || DUPLICATE_LOCKED_PRESETS.includes(preset);

/**
 * @param  {} userId
 * @param  {} usersPermissions
 * @description This function check given user is only have parent project permission
 *              (no access to create linked activity and other specific permission in program linked activities table)
 */
export const isOnlyProgramCollaborator = (userId, members) => {
  if (!userId && !members) {
    return true;
  }

  const userPermission = members.find((member) => member.userId === userId);

  if (!userPermission) {
    return true;
  }

  return get(userPermission, 'isOnlyProgramCollaborator', true);
};

/**
 * Check if individual user
 * @param {object} user
 * @returns
 */
export const isIndividualUser = (user) => {
  const orgType = get(user, 'organisation.type', null);
  return orgType && orgType === 'INDIVIDUAL';
};

/**
 * Check if user feature
 * @param {string} user
 * @param {string} featureName
 * @param {string} currentActivity
 *
 * @returns boolean
 */
export const isFeature = (user, featureName, currentActivity = {}) => {
  // skip permission check if user 'super admin'
  if (user.role === 'admin') return true;

  // skip permission check if invited activity, or if this user is the
  // actual owner of the activity. Ownership is enforced server-side
  // (including the root-organisation check for guest-owned linked
  // Programs — TAK-2040), so a personal feature flag is only meant to
  // gate who can CREATE a new activity of this type, not who can view or
  // manage one they already legitimately own.
  if (
    isInvitedActivityMember(user, currentActivity) ||
    (!isEmpty(currentActivity) && user.id === currentActivity.ownerId)
  ) {
    return true;
  }

  // check features permission
  const features = get(user, 'features', []);
  return features.includes(featureName);
};

export const getPermissionType = (permissions) => {
  let PermissionType = 'custom';
  // The Duplicate entry isn't a page, so it doesn't decide the preset.
  const permissionArray = withoutDuplicatePermission(permissions);

  const isReporter = isReporterPermission(permissionArray);
  const isDocumentor = isDocumentorPermission(permissionArray);
  const isCollaborator = isCollaboratorPermission(permissionArray);

  if (isReporter) {
    PermissionType = 'reportOnly';
  } else if (isDocumentor) {
    PermissionType = 'documentor';
  } else if (isCollaborator) {
    PermissionType = 'collaborator';
  } else {
    PermissionType = 'custom';
  }

  return PermissionType;
};

/**
 * check whether reporter permission type or not
 * @param  {array} permissionArray
 */
const isReporterPermission = (permissionArray) => {
  // TODO:perm
  // the logic below is pretty screwy - it assumes there is an order
  // to the permissions array, but this is flaky I reckon
  // (eg is array is stored as MySQL JSON, order cannot be controlled/guaranteed)
  for (const page of permissionArray) {
    const permission = get(page, 'permission');
    if (page.state === PERM_ACTIVITY_REPORT) {
      if (permission.read) {
        return true;
      } else {
        return false;
      }
    } else {
      if (!(!permission.write && !permission.read)) {
        return false;
      }
    }
  }
};

/**
 * check whether documentor permission type or not
 * @param  {array} permissionArray
 */
const isDocumentorPermission = (permissionArray) => {
  for (const page of permissionArray) {
    const permission = get(page, 'permission');
    if (page.state === PERM_ACTIVITY_REPORT && !permission.read) {
      return false;
    } else if (
      page.state === PERM_ACTIVITY_DOCUMENTATION &&
      (!permission.write || !permission.read)
    ) {
      return false;
    } else if (page.state === PERM_ACTIVITY_GREEN_LIGHT && !permission.read) {
      return false;
    } else {
      if (
        ![
          PERM_ACTIVITY_REPORT,
          PERM_ACTIVITY_GREEN_LIGHT,
          PERM_ACTIVITY_DOCUMENTATION,
        ].includes(page.state) &&
        !(!permission.write && !permission.read)
      ) {
        return false;
      }
    }
  }
  return true;
};

/**
 * check whether collaborator permission type or not
 * @param  {array} permissionArray
 */
const isCollaboratorPermission = (permissionArray) => {
  for (const page of permissionArray) {
    const permission = get(page, 'permission');
    if (page.state === PERM_ACTIVITY_REPORT && !permission.read) {
      return false;
    } else if (page.state === PERM_ACTIVITY_GREEN_LIGHT && !permission.read) {
      return false;
    } else {
      if (
        ![PERM_ACTIVITY_REPORT, PERM_ACTIVITY_GREEN_LIGHT].includes(
          page.state
        ) &&
        (!permission.write || !permission.read)
      ) {
        return false;
      }
    }
  }
  return true;
};

/**
 * check user is activity member or not
 * @param  {object} user
 * @param  {array} members
 *
 * @returns boolean
 */
export const isActivityMember = (user, members = []) => {
  const member = find(members, { userId: user.id });
  return Boolean(member);
};

/**
 * check user is invited activity member or not
 * @param  {object} user
 * @param  {object} currentActivity
 *
 * @returns boolean
 */
export const isInvitedActivityMember = (user, currentActivity = {}) => {
  if (isEmpty(currentActivity)) return false;

  const isMember = isActivityMember(user, currentActivity.members);
  if (user.id !== currentActivity.ownerId && isMember) {
    return true;
  }

  return false;
};

/**
 * check Member Permission
 * @param  {object} user
 * @param  {array} members
 * @param  {string} type
 *
 * @returns
 */
export const checkMemberPermission = (user, members, type) => {
  const member = find(members, { userId: user.id });

  if (member) {
    // TODO:perm
    // originally we did the lookup based on "name", which is completely
    // fkn mad, as that's a label - it should be done by state, which is closer to
    // a fixed id (e.g. project.edit.greenLight instead of "Green Light" or "GreenLight")
    const typePermission = find(member.permissions, { state: type });

    // Return default read-only permission if type not found (handles data migration issues)
    if (!typePermission) {
      console.warn(
        `Permission type "${type}" not found for user ${user.id}. Using default read-only.`
      );
      return { read: true, write: false };
    }

    return typePermission.permission;
  } else {
    // if not member only grant read permissions but if user hasn't any permission it will check in protected upper lever (restrict unauthorized access)
    return { read: true, write: false };
  }
};

/**
 * Check activity view permission
 * @param {object} user
 * @param {object} currentActivity
 * @returns
 */
export const checkPermissionDenied = (user, currentActivity) => {
  // skip permission check if invited activity, or if this user is the
  // actual owner of the activity. Ownership is enforced server-side
  // (including the root-organisation check for guest-owned linked
  // Programs — TAK-2040), so a personal feature flag is only meant to
  // gate who can CREATE a new activity of this type, not who can view or
  // manage one they already legitimately own. (Note: the isFeature() calls
  // below don't forward currentActivity, so isFeature's own owner-bypass
  // never applies here — this check has to be made directly.)
  if (
    isInvitedActivityMember(user, currentActivity) ||
    (currentActivity && user.id === currentActivity.ownerId)
  ) {
    return false;
  }

  if (currentActivity) {
    if (currentActivity.activityPlanTypeId === 1) {
      if (!isFeature(user, 'single_program')) return true;
    }
    if (currentActivity.activityPlanTypeId === 3) {
      if (!isFeature(user, 'festival_program')) return true;
    }
    if (currentActivity.activityPlanTypeId === 4) {
      if (!isFeature(user, 'grant_program')) return true;
    }
    if (currentActivity.activityPlanTypeId === 6) {
      if (!isFeature(user, 'multi_activity_program')) return true;
    }
  }
  return false;
};

export const checkGuestCollaborator = (activity, auth) => {
  const getCurrentMember = find(
    activity.currentActivity.members,
    (mem) => mem.userId === auth.user.id
  );
  let isGuestCollaborator =
    getPermissionType(getCurrentMember.permissions) === 'collaborator';

  return isGuestCollaborator;
};
